import {
    Fragment,
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type {
    StepperProps,
    StepperRef,
    StepId,
    UpdateConfigFunc,
    StepWithStatusesAndActions,
    SaveButtonOption,
} from './types';
// Import Helpers
import { getStepsInitialValues, getStepsInitialConfigs } from './helpers';
// Import Providers
import { StepperProvider, useStepperContext } from './context';
// Import Components
import StepperNavigation from './StepperNavigation';
import StepContainer from './StepContainer';
// Import Styles
import styles from './stepper.module.scss';

const Stepper = forwardRef<StepperRef, StepperProps>(
    (
        {
            mode,
            steps,
            initialActiveStepId,
            isLoading,
            onSubmittingChange,
            onActiveStepActionsChange,
            onActiveStepChange,
            onStepsValuesChange,
            pendingStepValues,
            onStepValuesApplied,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const [activeStepId, setActiveStepId] = useState<StepId>();
        const { allStepsValues } = useStepperContext();

        const [stepperSubmitCount, setStepperSubmitCount] = useState<number>(0);
        const [hasOngoingRequest, setHasOngoingRequest] = useState<boolean>(false);
        const [stepsFullConfigs, setStepsFullConfigs] = useState<StepWithStatusesAndActions[]>(
            getStepsInitialConfigs(steps, t),
        );
        const [pressedSubmitButton, setPressedSubmitButton] = useState<SaveButtonOption | null>(null);
        const isStepperSubmitting = !!pressedSubmitButton;

        useEffect(() => {
            onSubmittingChange?.(isStepperSubmitting);
        }, [isStepperSubmitting, onSubmittingChange]);

        // Keyed by content so the arrays keep their identity while validation flags churn in `stepsFullConfigs`
        const mountedStepIdsKey = stepsFullConfigs
            .filter(({ isMounted }) => isMounted)
            .map(({ id }) => id)
            .join(',');
        const dirtyStepIdsKey = stepsFullConfigs
            .filter(({ isMounted, isDirty }) => isMounted && isDirty)
            .map(({ id }) => id)
            .join(',');

        const mountedStepIds = useMemo(
            () => stepsFullConfigs.filter(({ isMounted }) => isMounted).map(({ id }) => id),
            // DEPS: [stepsFullConfigs] — recomputed only when the id list changes, see the key above
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [mountedStepIdsKey],
        );

        const dirtyStepIds = useMemo(
            () => stepsFullConfigs.filter(({ isMounted, isDirty }) => isMounted && isDirty).map(({ id }) => id),
            // DEPS: [stepsFullConfigs] — recomputed only when the id list changes, see the key above
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [dirtyStepIdsKey],
        );

        useEffect(() => {
            onStepsValuesChange?.(allStepsValues, { mountedStepIds, dirtyStepIds });
        }, [allStepsValues, dirtyStepIds, mountedStepIds, onStepsValuesChange]);

        // Apply pending values (e.g. a restored draft) to each step once it has registered its `applyValues`
        // action, which happens after the step is mounted and its form is initialised.
        const appliedStepIdsRef = useRef<Set<StepId>>(new Set());

        useEffect(() => {
            if (isNil(pendingStepValues)) {
                appliedStepIdsRef.current.clear();
                return;
            }

            stepsFullConfigs.forEach(({ id, applyValues }) => {
                const stepPendingValues = pendingStepValues[id];
                if (isNil(stepPendingValues) || !applyValues || appliedStepIdsRef.current.has(id)) {
                    return;
                }

                appliedStepIdsRef.current.add(id);
                applyValues(stepPendingValues);
                onStepValuesApplied?.(id);
            });
        }, [onStepValuesApplied, pendingStepValues, stepsFullConfigs]);

        const activeStepConfigs = useMemo(
            () => stepsFullConfigs.find(({ id }) => id === activeStepId) || null,
            [activeStepId, stepsFullConfigs],
        );

        const hasClear = !!activeStepConfigs?.clear;
        const hasReset = !!activeStepConfigs?.reset;

        useEffect(() => {
            onActiveStepActionsChange?.({
                hasClear,
                hasReset,
            });
        }, [hasClear, hasReset, onActiveStepActionsChange]);

        const hasPendingStep = stepsFullConfigs.some(
            ({ isValidating, isLoading, isValid }) => isNil(isValid) || isValidating || isLoading,
        );

        const hasValidatedStepWithInvalid = stepsFullConfigs.some(
            ({ isValidating, isLoading, isValid }) => !isValidating && !isLoading && isValid === false,
        );

        useEffect(() => {
            // if the stepper is in submitting process and either there is an invalid step (even when other steps are still initiating) or all steps are ready

            if (pressedSubmitButton && (hasValidatedStepWithInvalid || !hasPendingStep)) {
                if (!pressedSubmitButton.ignoreInvalidSteps && hasValidatedStepWithInvalid) {
                    setPressedSubmitButton(null);
                } else {
                    // this is just to ensure that it is not requesting during ongoing request,
                    // actually we don't need this check, just we keep it to prevent us from making double requests because of bugs )))
                    if (hasOngoingRequest) {
                        return;
                    }

                    setHasOngoingRequest(true);
                    pressedSubmitButton.handler(allStepsValues).finally(() => {
                        setPressedSubmitButton(null);
                        setHasOngoingRequest(false);
                    });
                }
            }
        }, [allStepsValues, hasOngoingRequest, hasPendingStep, hasValidatedStepWithInvalid, pressedSubmitButton]);

        const updateConfig = useCallback<UpdateConfigFunc>((stepId, newConfig) => {
            setStepsFullConfigs((prev) =>
                prev.map((stepConfig) => (stepConfig.id === stepId ? { ...stepConfig, ...newConfig } : stepConfig)),
            );
        }, []);

        const stepChangeHandler = useCallback(
            (stepId: StepId) => {
                updateConfig(stepId, { isMounted: true });
                setActiveStepId(stepId);
                onActiveStepChange?.(stepId);
            },
            [updateConfig, onActiveStepChange],
        );

        // Select first step as active on mount
        useLayoutEffect(() => {
            stepChangeHandler(initialActiveStepId);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleStepperSubmit = useCallback((buttonOption) => {
            setStepsFullConfigs((prev) => prev.map((stepConfigs) => ({ ...stepConfigs, isMounted: true })));
            setPressedSubmitButton(buttonOption);
            setStepperSubmitCount((prev) => prev + 1);
        }, []);

        const handleStepperResetAll = useCallback(() => {
            stepsFullConfigs.forEach(({ isMounted, reset, clear }) => {
                if (isMounted) {
                    (reset ?? clear)?.();
                }
            });
        }, [stepsFullConfigs]);

        useImperativeHandle(
            ref,
            () => ({
                submit: handleStepperSubmit,
                clear: activeStepConfigs?.clear ?? null,
                reset: activeStepConfigs?.reset ?? null,
                resetAll: handleStepperResetAll,
            }),
            [handleStepperSubmit, handleStepperResetAll, activeStepConfigs],
        );

        return (
            <div className={styles.container}>
                <StepperNavigation
                    mode={mode}
                    isLoading={isLoading}
                    activeStep={activeStepId}
                    stepsFullConfigs={stepsFullConfigs}
                    onChange={stepChangeHandler}
                />

                <div className={classNames(styles.content, 'crm-scroll-holder')}>
                    <div className="crm-scrollable-container">
                        {stepsFullConfigs.map((stepConfig) => {
                            const isActiveStep = stepConfig.id === activeStepId;
                            return (
                                (isActiveStep || stepConfig.isMounted) && (
                                    <Fragment key={stepConfig.id}>
                                        <div
                                            className={classNames(styles.stepContainer, {
                                                [styles.hideContent]: !isActiveStep /* || stepConfig.isLoading*/,
                                                [styles.disableContent]: isStepperSubmitting,
                                            })}
                                        >
                                            <StepContainer
                                                {...stepConfig}
                                                mode={mode}
                                                activeStepId={activeStepId}
                                                updateConfig={updateConfig}
                                                stepperSubmitCount={stepperSubmitCount}
                                            />
                                        </div>
                                    </Fragment>
                                )
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    },
);

Stepper.displayName = 'Stepper';

const StepperContainer = forwardRef<StepperRef, StepperProps>((props, ref) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stepsInitialValues = useMemo(() => getStepsInitialValues(props.steps), []);

    return (
        <StepperProvider stepsInitialValues={stepsInitialValues}>
            <Stepper ref={ref} {...props} />
        </StepperProvider>
    );
});

StepperContainer.displayName = 'StepperContainer';

export default memo(StepperContainer);
